import React, { useEffect, useState } from 'react';
import candidateAvatar from '../../assets/img/avatar-grey.png';
import companyAvatar from '../../assets/img/company-logo-grey.svg';
import imgNoResult from '../../assets/img/offer-no-result.svg';
import {
    PartnerReportedDetailsRequestType,
    PartnerReportedDetailsResponseType,
    ReportedActionRequestType,
    ReportedDetailsRequestType,
    ReportedDetailsResponseType,
    ReportReviewType,
    ReviewAndCommentSuspendStatus,
} from '../../types/report';
import { reportService } from '../../lib/api/report';
import RatingStar from '../../components/rating-star';
import { ContractComplianceStateEnum } from '../../types/review';
import { format, parseISO } from 'date-fns';
import PageLoader from '../../components/loader';
import { ErrorIcon, Message, SuccessIcon, SweetAlertTitle, WarningIcon } from '../../lib/utils/messages';
import Swal from 'sweetalert2';
import { partnerReportService } from '../../lib/api/partnerReport';

type Props = {
    type: 'reviewReport' | 'replyReport';
    reportedData: ReportedDetailsRequestType | PartnerReportedDetailsRequestType | null;
    actionData: ReportedActionRequestType | null;
    suspendReasonText: string;
    handleClose: () => void;
    getList: () => void;
    isPartner: boolean;
};

const ReviewBody: React.FC<Props> = ({
    reportedData,
    type,
    actionData,
    suspendReasonText,
    handleClose,
    getList,
    isPartner,
}) => {
    const [reportedReviews, setReportedReviews] = useState<ReportedDetailsResponseType | null>(null);
    const [partnerReportedReviews, setPartnerReportedReviews] = useState<PartnerReportedDetailsResponseType | null>(
        null,
    );
    const [loading, setLoading] = useState(false);
    const [submitData, setSubmitData] = useState(false);
    const reportedActions = async (suspendStatus: ReviewAndCommentSuspendStatus) => {
        try {
            setSubmitData(true);
            if (actionData) {
                const params = {
                    offerId: actionData?.offerId || '',
                    replyId: actionData?.replyId || null,
                    reportReplyId: actionData?.reportReplyId || null,
                    reviewId: actionData?.reviewId || '',
                    reportReviewId: actionData?.reportReviewId || null,
                    reviewReplyText: actionData?.reviewReplyText,
                    reviewType: actionData?.reviewType || 0,
                    suspendStatus,
                };

                const result = await reportService.setReportedAction(params);
            }

            setSubmitData(false);
            Swal.fire({
                title: SweetAlertTitle.Success,
                text: suspendStatus === ReviewAndCommentSuspendStatus.ActionTaken ? Message.Approved : Message.Rejected,
                icon: SuccessIcon,
                showConfirmButton: true,
                showCloseButton: false,
                allowOutsideClick: false,
            }).then((result: any) => {
                if (result.isConfirmed) {
                    setTimeout(() => {
                        handleClose();
                        getList();
                    }, 1000);
                }
            });
        } catch (error) {
            setSubmitData(false);
            console.log(error);
            Swal.fire({
                title: SweetAlertTitle.Error,
                text: Message.SomethingWentWrong,
                icon: ErrorIcon,
                showConfirmButton: true,
                showCloseButton: false,
                allowOutsideClick: false,
            }).then((result: any) => {
                if (result.isConfirmed) {
                    setTimeout(() => {
                        handleClose();
                    }, 1000);
                }
            });
        }
    };
    const partnerReportedActions = async (toRetain: boolean, isReviewReporting: boolean) => {
        try {
            setSubmitData(true);
            if (actionData) {
                const params = {
                    ...(isReviewReporting
                        ? { partnerReviewId: actionData?.reviewId || '' }
                        : { partnerReplyId: actionData?.replyId || '' }),

                    isReportRetained: toRetain ? true : false,
                };

                const result = isReviewReporting
                    ? await partnerReportService.setPartnerReviewReportedAction(params)
                    : await partnerReportService.setPartnerReplyReportedAction(params);
            }

            setSubmitData(false);
            Swal.fire({
                title: SweetAlertTitle.Success,
                text: !toRetain ? Message.Approved : Message.Rejected,
                icon: SuccessIcon,
                showConfirmButton: true,
                showCloseButton: false,
                allowOutsideClick: false,
            }).then((result: any) => {
                if (result.isConfirmed) {
                    setTimeout(() => {
                        handleClose();
                        getList();
                    }, 1000);
                }
            });
        } catch (error) {
            setSubmitData(false);
            console.log(error);
            Swal.fire({
                title: SweetAlertTitle.Error,
                text: Message.SomethingWentWrong,
                icon: ErrorIcon,
                showConfirmButton: true,
                showCloseButton: false,
                allowOutsideClick: false,
            }).then((result: any) => {
                if (result.isConfirmed) {
                    setTimeout(() => {
                        handleClose();
                    }, 1000);
                }
            });
        }
    };

    const splitSuspendedReasons = (reasons: string) => {
        const parts = reasons?.split(', ');
        const otherIndex = parts.findIndex((reason) => reason.startsWith('Other -'));

        if (otherIndex !== -1) {
            const otherReason = parts.slice(otherIndex).join(', ').replace('Other - ', ''); // Remove "Other - "
            return {
                reasons: parts.slice(0, otherIndex), // Normal reasons as an array
                otherReason: otherReason, // The cleaned other reason
            };
        }

        return { reasons: parts, otherReason: null };
    };

    const { reasons, otherReason } = (suspendReasonText && splitSuspendedReasons(suspendReasonText)) || {};

    const showDialogueBox = (type: string, tabName: string) => {
        Swal.fire({
            title: SweetAlertTitle.Warning,
            text: type === 'retain' ? Message.ConfirmRetain : Message.ConfirmDelete,
            icon: WarningIcon,
            showConfirmButton: true,
            showCancelButton: true,
            showCloseButton: false,
            allowOutsideClick: false,
        }).then((result: any) => {
            if (result.isConfirmed) {
                const toReportReview = tabName == 'reviewReport' ? true : false;

                if (type === 'retain') {
                    if (isPartner) {
                        partnerReportedActions(true, toReportReview);
                    } else {
                        reportedActions(ReviewAndCommentSuspendStatus.ActionNotTaken);
                    }
                } else {
                    if (isPartner) {
                        partnerReportedActions(false, toReportReview);
                    } else {
                        reportedActions(ReviewAndCommentSuspendStatus.ActionTaken);
                    }
                }
            }
        });
    };

    useEffect(() => {
        const getResponseData = async () => {
            try {
                setLoading(true);
                if (reportedData) {
                    const { resultObject: resultObject } = isPartner
                        ? await partnerReportService.getPartnerReportedDetailsList({
                              partnerReviewId: reportedData.reviewId || '',
                              partnerReplyId: reportedData.replyId || '',
                          })
                        : await reportService.getReportedDetailsList(reportedData);
                    if (resultObject) {
                        if (resultObject) {
                            if (isPartner) {
                                setPartnerReportedReviews(resultObject[0]);
                            } else {
                                setReportedReviews(resultObject);
                            }
                        }
                    }
                }

                setLoading(false);
            } catch (error) {
                console.log(error);
                setLoading(false);
            }
        };

        getResponseData();
    }, [reportedData]);

    const shouldShowUI = isPartner
        ? !partnerReportedReviews
        : !reportedReviews || Object.values(reportedReviews).length === 0;

    if (shouldShowUI) {
        if (loading) {
            return (
                <>
                    <div className="new-spinner p-absolute">
                        <div>
                            <span className="spinner-border spinner-border-sm custom-spinner-border" />
                        </div>
                        <p className="fs-14 custom-loading-text">Loading</p>
                    </div>
                </>
            );
        } else {
            return <div className="m-auto text-center fs-18">No data found</div>;
        }
    }

    const {
        contractComplianceState,
        jobStartDate,
        jobTitle,
        replies,
        reviewText,
        reviewerAvatarUrl,
        revieweeAvatarUrl,
        reviewerName,
        stars,
        id,
        isOnboardingReview,
    } = reportedReviews ?? {};

    const {
        contractComplianceState: partnerContractComplianceState,
        reportedOnCompanyName,
        modifiedDate,
        partnerReplies,
        rating,
        reportStatus,
        reviewReportStatus,
        reviewType: partnerReviewType = 1,
        suspendedReasons,
        reviewText: partnerReviewText,
        isOnboardingReview: partnerIsOnboardingReview = true,
        id: partnerId,
        reportedByAvatarUrl,
        reportedOnAvatarUrl,
    } = partnerReportedReviews ?? {};

    const getTheActionBtns = (suspendedReasons?: any, reportedOtherText?: any) => {
        return (
            <div className="reported-reasons ms-5 mt-3 mb-4">
                <div>
                    <strong>Reported Reasons:</strong>
                    {isPartner ? (
                        <>
                            <ul className="mt-1 fs-12">
                                {suspendedReasons.length === 0 && !reportedOtherText ? (
                                    <li>No reasons found</li>
                                ) : (
                                    <>
                                        {suspendedReasons.length > 0 &&
                                            suspendedReasons.map((reason: any, index: number) => (
                                                <li key={index}>{reason?.suspendedReasonText}.</li>
                                            ))}

                                        {reportedOtherText && <li>{reportedOtherText}</li>}
                                    </>
                                )}
                            </ul>
                        </>
                    ) : (
                        <>
                            {reasons && reasons.length > 0 && (
                                <ul className="mt-1 fs-12">
                                    {reasons.map((reason, index) => (
                                        <li key={index}>{reason}.</li>
                                    ))}
                                </ul>
                            )}
                            {otherReason && (
                                <div className="mt-1 ms-3 fs-12">
                                    <strong>Other Reason:</strong> {otherReason}
                                </div>
                            )}
                        </>
                    )}
                </div>
                {actionData?.currentStatus === 1 && (
                    <div className="text-end mt-3">
                        <button
                            className="btn btn-success btn-sm me-3"
                            onClick={() => {
                                showDialogueBox('retain', type);
                            }}
                            disabled={submitData}
                        >
                            Retain
                        </button>
                        <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => {
                                showDialogueBox('delete', type);
                            }}
                            disabled={submitData}
                        >
                            Delete
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const unifiedReviewType = isPartner ? 1 : actionData?.reviewType;
    const unifiedContractComplianceState = isPartner
        ? partnerContractComplianceState === ContractComplianceStateEnum.ContractCompliance
        : contractComplianceState === ContractComplianceStateEnum.ContractCompliance;

    const unifiedId = isPartner ? partnerId : id;
    const unifiedReviewText = isPartner ? partnerReviewText : reviewText;
    const unifiedDate = isPartner ? modifiedDate : jobStartDate;

    return (
        <>
            {submitData && (
                <>
                    <div className="new-spinner p-absolute">
                        <div>
                            <span className="spinner-border spinner-border-sm custom-spinner-border" />
                        </div>
                        <p className="fs-14 custom-loading-text">Loading</p>
                    </div>
                </>
            )}
            <div className="tab-content" id="motoTabContent">
                <div className="tab-pane fade active show" role="tabpanel" aria-labelledby="moto-tab">
                    <div>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <div className="d-flex">
                                    <img
                                        // src={
                                        //     reviewerAvatarUrl ||
                                        //     (actionData?.reviewType === ReportReviewType.CandidateReview
                                        //         ? candidateAvatar
                                        //         : companyAvatar)
                                        // }
                                        src={
                                            actionData?.reviewType === ReportReviewType.CompanyReview
                                                ? reviewerAvatarUrl || candidateAvatar
                                                : reviewerAvatarUrl || reportedOnAvatarUrl || companyAvatar
                                        }
                                        alt=""
                                        className="avatar avatar--xxss me-2"
                                    />
                                    <div className="d-flex flex-column">
                                        <div className="fs-14 fw-600">
                                            <div title="">{reportedOnCompanyName || ''}</div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <div className="pt-1 me-1 lt-text-primary-light fs-20 fw-600">
                                                {stars?.value || rating || 0}
                                            </div>
                                            <RatingStar value={stars?.value || rating || 0} size={22} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex">
                                {/* <div
                                    className={`lt-status ${contractComplianceState === ContractComplianceStateEnum.ContractCompliance ? 'lt-status-error' : 'lt-status-success'} lt-status-pill fs-12 px-2 py-1 mb-0`}
                                    style={{ width: 'max-content' }}
                                >
                                    {contractComplianceState === ContractComplianceStateEnum.ContractCompliance ? (
                                        actionData?.reviewType === ReportReviewType.CandidateReview ? (
                                            <>
                                                <i className="bi bi-dash-circle me-1"></i> Ghosted{' '}
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-dash-circle me-1"></i> Offer Violated
                                            </>
                                        )
                                    ) : actionData?.reviewType === ReportReviewType.CandidateReview ? (
                                        <>
                                            <i className="bi bi-check-circle me-1"></i> Onboarded{' '}
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check-circle me-1"></i> Offer Compliant{' '}
                                        </>
                                    )}
                                </div> */}

                                {/* {contractComplianceState === ContractComplianceStateEnum.ContractCompliance ? (
                                    <div
                                        className="lt-status lt-status-success lt-status-pill fs-12 px-2 py-1 mb-0"
                                        style={{ width: 'max-content' }}
                                    >
                                        <i className="bi-check-circle me-1"></i>
                                        {actionData?.reviewType === ReportReviewType.CompanyReview
                                            ? 'Onboarded'
                                            : 'Offer Compliant'}
                                    </div>
                                ) : contractComplianceState === ContractComplianceStateEnum.ContractViolation ? (
                                    <div
                                        className="lt-status lt-status-error lt-text-error lt-status-pill fs-12 px-2 py-1 mb-0"
                                        style={{ width: 'max-content' }}
                                    >
                                        <i className="bi-dash-circle me-1"></i>
                                        {actionData?.reviewType === ReportReviewType.CandidateReview
                                            ? 'Ghosted'
                                            : 'Offer Violated'}
                                    </div>
                                ) : null} */}

                                {isOnboardingReview || partnerIsOnboardingReview ? (
                                    unifiedReviewType === ReportReviewType.CandidateReview ? (
                                        unifiedContractComplianceState ? (
                                            <div
                                                className="lt-status lt-status-success lt-status-pill fs-12 px-2 py-1 mb-0"
                                                style={{ width: 'max-content' }}
                                            >
                                                <i className="bi-check-circle me-1"></i> Onboarded
                                            </div>
                                        ) : (
                                            <div
                                                className="lt-status lt-status-error lt-text-error lt-status-pill fs-12 px-2 py-1 mb-0"
                                                style={{ width: 'max-content' }}
                                            >
                                                <i className="bi-dash-circle me-1"></i> Ghosted
                                            </div>
                                        )
                                    ) : unifiedReviewType === ReportReviewType.CompanyReview ? (
                                        unifiedContractComplianceState ? (
                                            <div
                                                className="lt-status lt-status-success lt-status-pill fs-12 px-2 py-1 mb-0"
                                                style={{ width: 'max-content' }}
                                            >
                                                <i className="bi-check-circle me-1"></i> Offer Compliant
                                            </div>
                                        ) : (
                                            <div
                                                className="lt-status lt-status-error lt-text-error lt-status-pill fs-12 px-2 py-1 mb-0"
                                                style={{ width: 'max-content' }}
                                            >
                                                <i className="bi-dash-circle me-1"></i> Offer Violated
                                            </div>
                                        )
                                    ) : null
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                        <div
                            className={`review-card lt-shadow-sm ms-5 mt-3 mb-4 ${type === 'reviewReport' && unifiedId === actionData?.reviewId ? 'reported-div' : ''} `}
                        >
                            <div className="review-card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h3 className="lt-text-primary fs-14 fw-600 mb-1" title="">
                                            {jobTitle || ''}
                                        </h3>
                                        <div className="fs-10 lt-text-secondary">
                                            {unifiedDate && format(parseISO(unifiedDate), 'dd MMM yyyy')}
                                        </div>
                                    </div>
                                </div>

                                <div className="fs-12 mt-2">{unifiedReviewText}</div>
                            </div>
                        </div>

                        {type === 'reviewReport' &&
                            (isPartner ? true : unifiedId === actionData?.reviewId) &&
                            getTheActionBtns(suspendedReasons || [], actionData?.partnerReviewReportedText)}

                        {isPartner ? (
                            <>
                                {partnerReplies?.map((item) => {
                                    const { replyText, replyDate, avatarUrl, companyName } = item;

                                    return (
                                        <div className="reply-v-line">
                                            <div className="offcanvas-reply mb-4">
                                                <div className="reply-h-line">
                                                    <div className="d-flex ms-4 mb-3 review-card lt-shadow-sm">
                                                        <div
                                                            className={`${type === 'replyReport' && item?.id === actionData?.replyId ? 'reported-div' : ''}`}
                                                        >
                                                            <div className={`review-card-body`}>
                                                                <div className="d-flex justify-content-between">
                                                                    <div className="d-flex align-items-center">
                                                                        <img
                                                                            src={avatarUrl || companyAvatar}
                                                                            alt=""
                                                                            className="avatar avatar--xxss me-2"
                                                                        />

                                                                        <div>
                                                                            <h3 className="lt-text-primary fs-14 fw-600 mb-0">
                                                                                {companyName || 'Company name'}
                                                                            </h3>

                                                                            <div className="lt-text-secondary fs-10 fw-400">
                                                                                Replied on{' '}
                                                                                {format(
                                                                                    parseISO(replyDate),
                                                                                    'dd MMM yyyy',
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="fs-12 mt-2">{replyText}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {type === 'replyReport' && item?.id === actionData?.replyId ? (
                                                        <>
                                                            {type === 'replyReport' &&
                                                                item?.id === actionData?.replyId &&
                                                                getTheActionBtns(
                                                                    item?.suspendedReasons || [],
                                                                    item?.partnerReplyReportedText,
                                                                )}
                                                        </>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}{' '}
                            </>
                        ) : (
                            <>
                                {replies?.map((item) => {
                                    const {
                                        candidateFirstName,
                                        candidateLastName,
                                        date,
                                        replyText,
                                        candidateAvatarUrl,
                                        companyAvatarUrl,
                                        repliedByRole,
                                        companyName,
                                    } = item;

                                    return (
                                        <div className="reply-v-line">
                                            <div className="offcanvas-reply mb-4">
                                                <div className="reply-h-line">
                                                    <div className="d-flex ms-4 mb-3 review-card lt-shadow-sm">
                                                        <div
                                                            className={`${type === 'replyReport' && item?.id === actionData?.replyId ? 'reported-div' : ''}`}
                                                        >
                                                            <div className={`review-card-body`}>
                                                                <div className="d-flex justify-content-between">
                                                                    <div className="d-flex align-items-center">
                                                                        <img
                                                                            src={
                                                                                repliedByRole == 1
                                                                                    ? candidateAvatarUrl ||
                                                                                      candidateAvatar
                                                                                    : companyAvatarUrl || companyAvatar
                                                                            }
                                                                            alt=""
                                                                            className="avatar avatar--xxss me-2"
                                                                        />

                                                                        <div>
                                                                            <h3 className="lt-text-primary fs-14 fw-600 mb-0">
                                                                                {repliedByRole == 0
                                                                                    ? companyName
                                                                                    : candidateFirstName +
                                                                                      ' ' +
                                                                                      candidateLastName}
                                                                            </h3>

                                                                            <div className="lt-text-secondary fs-10 fw-400">
                                                                                Replied on{' '}
                                                                                {format(parseISO(date), 'dd MMM yyyy')}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="fs-12 mt-2">{replyText}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {type === 'replyReport' && item?.id === actionData?.replyId ? (
                                                        <>
                                                            {type === 'replyReport' &&
                                                                id === actionData?.reviewId &&
                                                                getTheActionBtns()}
                                                        </>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReviewBody;
